/**
 * Monitoring Design System
 * Theme-aware colors, animations, sx-style factories for all monitoring dashboards.
 * Pure TypeScript — no React components.
 */

import type { SxProps, Theme } from '@mui/material';

// ---------------------------------------------------------------------------
// 1. Theme-aware status colors
// ---------------------------------------------------------------------------

export interface MonitoringColors {
  healthy: string;
  healthyBg: string;
  healthyGlow: string;
  warning: string;
  warningBg: string;
  warningGlow: string;
  critical: string;
  criticalBg: string;
  criticalGlow: string;
  unknown: string;
  unknownBg: string;
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  surfaceHover: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
}

export const getMonitoringColors = (mode: 'light' | 'dark'): MonitoringColors => {
  if (mode === 'dark') {
    return {
      healthy: '#00D68F',
      healthyBg: 'rgba(0,214,143,0.08)',
      healthyGlow: '0 0 20px rgba(0,214,143,0.25)',
      warning: '#F4B942',
      warningBg: 'rgba(244,185,66,0.08)',
      warningGlow: '0 0 20px rgba(244,185,66,0.25)',
      critical: '#FF4757',
      criticalBg: 'rgba(255,71,87,0.08)',
      criticalGlow: '0 0 20px rgba(255,71,87,0.25)',
      unknown: '#8892A4',
      unknownBg: 'rgba(136,146,164,0.08)',
      pageBg: '#0A0E1A',
      cardBg: '#111827',
      cardBorder: 'rgba(255,255,255,0.07)',
      surfaceHover: 'rgba(255,255,255,0.04)',
      textPrimary: '#F9FAFB',
      textSecondary: '#9CA3AF',
      textMuted: '#6B7280',
    };
  }
  return {
    healthy: '#059669',
    healthyBg: 'rgba(5,150,105,0.06)',
    healthyGlow: '0 2px 8px rgba(5,150,105,0.15)',
    warning: '#d97706',
    warningBg: 'rgba(217,119,6,0.06)',
    warningGlow: '0 2px 8px rgba(217,119,6,0.15)',
    critical: '#dc2626',
    criticalBg: 'rgba(220,38,38,0.06)',
    criticalGlow: '0 2px 8px rgba(220,38,38,0.15)',
    unknown: '#6B7280',
    unknownBg: 'rgba(107,114,128,0.06)',
    pageBg: '#F3F4F6',
    cardBg: '#FFFFFF',
    cardBorder: '#E5E7EB',
    surfaceHover: 'rgba(0,0,0,0.02)',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
  };
};

// ---------------------------------------------------------------------------
// 2. Keyframe objects (for MUI sx @keyframes)
// ---------------------------------------------------------------------------

export const livePulse = {
  '@keyframes livePulse': {
    '0%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.4)', opacity: 0.6 },
    '100%': { transform: 'scale(1)', opacity: 1 },
  },
};

export const criticalPulse = {
  '@keyframes criticalPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255,71,87,0.4)' },
    '70%': { boxShadow: '0 0 0 12px rgba(255,71,87,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255,71,87,0)' },
  },
};

export const fadeInUp = {
  '@keyframes fadeInUp': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

// ---------------------------------------------------------------------------
// 3. sx-style factory functions
// ---------------------------------------------------------------------------

type StatusKey = 'healthy' | 'warning' | 'critical' | 'unknown';

export const heroBannerSx = (status: StatusKey, mode: 'light' | 'dark'): SxProps<Theme> => {
  const c = getMonitoringColors(mode);
  const statusColor = c[status];
  const gradientMap: Record<StatusKey, string> = {
    healthy: mode === 'dark'
      ? `linear-gradient(135deg, ${c.healthy}, #0D9488)`
      : `linear-gradient(135deg, #059669, #10b981)`,
    warning: mode === 'dark'
      ? `linear-gradient(135deg, ${c.warning}, #D97706)`
      : `linear-gradient(135deg, #d97706, #f59e0b)`,
    critical: mode === 'dark'
      ? `linear-gradient(135deg, ${c.critical}, #DC2626)`
      : `linear-gradient(135deg, #dc2626, #ef4444)`,
    unknown: mode === 'dark'
      ? `linear-gradient(135deg, ${c.unknown}, #4B5563)`
      : `linear-gradient(135deg, #6B7280, #9CA3AF)`,
  };

  return {
    background: gradientMap[status],
    borderRadius: 3,
    p: 3,
    mb: 3,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: mode === 'dark'
      ? `0 4px 30px rgba(0,0,0,0.4), ${status !== 'unknown' ? `0 0 40px ${statusColor}22` : 'none'}`
      : '0 4px 20px rgba(0,0,0,0.12)',
    ...(status === 'critical' ? {
      ...criticalPulse,
      animation: 'criticalPulse 2s infinite',
    } : {}),
  };
};

export const kpiCardSx = (accentColor: string, mode: 'light' | 'dark'): SxProps<Theme> => {
  const c = getMonitoringColors(mode);
  return {
    borderRadius: 3,
    bgcolor: c.cardBg,
    border: `1px solid ${c.cardBorder}`,
    borderBottom: `3px solid ${accentColor}`,
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    ...fadeInUp,
    animation: 'fadeInUp 0.4s ease-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: mode === 'dark'
        ? `0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px ${accentColor}22`
        : `0 8px 24px rgba(0,0,0,0.12)`,
    },
  };
};

export const chartCardSx = (mode: 'light' | 'dark'): SxProps<Theme> => {
  const c = getMonitoringColors(mode);
  return {
    borderRadius: 3,
    bgcolor: c.cardBg,
    border: `1px solid ${c.cardBorder}`,
    boxShadow: mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
  };
};

export const serviceRowSx = (status: StatusKey, mode: 'light' | 'dark'): SxProps<Theme> => {
  const c = getMonitoringColors(mode);
  const colorMap: Record<StatusKey, string> = {
    healthy: c.healthy,
    warning: c.warning,
    critical: c.critical,
    unknown: c.unknown,
  };
  return {
    borderLeft: `4px solid ${colorMap[status]}`,
    '&:hover': {
      bgcolor: c.surfaceHover,
    },
    transition: 'background-color 0.15s ease',
  };
};

export const liveBadgeSx = (): SxProps<Theme> => ({
  bgcolor: 'rgba(0,214,143,0.12)',
  color: '#00D68F',
  fontWeight: 700,
  fontSize: '0.65rem',
  letterSpacing: '0.5px',
  height: 22,
  '& .MuiChip-label': { px: 1 },
});

export const realtimeBadgeSx = (): SxProps<Theme> => ({
  bgcolor: 'rgba(99,102,241,0.12)',
  color: '#6366F1',
  fontWeight: 700,
  fontSize: '0.65rem',
  letterSpacing: '0.5px',
  height: 22,
  '& .MuiChip-label': { px: 1 },
});

// ---------------------------------------------------------------------------
// 4. Chart helpers
// ---------------------------------------------------------------------------

export const getChartTheme = (mode: 'light' | 'dark') => {
  if (mode === 'dark') {
    return {
      gridColor: 'rgba(255,255,255,0.06)',
      tickColor: '#9CA3AF',
      tooltipBg: '#1F2937',
      tooltipTitleColor: '#F9FAFB',
      tooltipBodyColor: '#D1D5DB',
      tooltipBorderColor: 'rgba(255,255,255,0.1)',
    };
  }
  return {
    gridColor: 'rgba(0,0,0,0.06)',
    tickColor: '#6B7280',
    tooltipBg: '#FFFFFF',
    tooltipTitleColor: '#111827',
    tooltipBodyColor: '#374151',
    tooltipBorderColor: '#E5E7EB',
  };
};

// Monospace for numeric values
export const monoValueSx: SxProps<Theme> = {
  fontFamily: '"Fira Code", "Roboto Mono", monospace',
  fontWeight: 700,
};

// ---------------------------------------------------------------------------
// 5. Status normalizer
// ---------------------------------------------------------------------------

export const mapStatus = (status: string): StatusKey => {
  const s = status?.toLowerCase() || '';
  if (['operational', 'healthy', 'up', 'ok', 'active', 'running', 'valid', 'success', 'green'].includes(s)) return 'healthy';
  if (['degraded', 'slow', 'warning', 'pending', 'expiring', 'yellow', 'provisioning'].includes(s)) return 'warning';
  if (['outage', 'unhealthy', 'down', 'error', 'alarm', 'failed', 'stopped', 'expired', 'red'].includes(s)) return 'critical';
  return 'unknown';
};
